/* eslint-disable no-empty */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import "./UpsellOrderForm.scss";

const DEFAULT_REDIRECT = "https://app.unclekam.com/thank-you";

// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

async function callApi(PROXY_URL, { method = "GET", path, qa_mode, data, params }) {
  const body = { path, method, qa_mode, ...(data ? { data } : {}), ...(params ? { params } : {}) };
  const resp = await axios.post(PROXY_URL, body, { headers: { "Content-Type": "application/json" } });
  return resp;
}

function getEffectivePrice(p) {
  const d = Number.parseInt(String(p?.discounted_price ?? "").trim(), 10);
  const base = Number.parseInt(String(p?.price ?? "0").trim(), 10) || 0;
  return Number.isFinite(d) && d > 0 ? d : base;
}

function formatMoney(n) {
  const x = Number(n || 0);
  const s = x.toFixed(2);
  return s.endsWith(".00") ? String(Math.round(x)) : s;
}

const stripeInputStyle = {
  style: {
    base: {
      fontSize: "14px",
      color: "#212529",
      "::placeholder": { color: "#ADB5BD" },
      fontFamily:
        'Open Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
    },
    invalid: { color: "#dc3545" },
  },
};

function UpsellOrderFormInner({
  productId,
  upsellPurchasePath = "/onclick-upsell",
  redirectUrl = DEFAULT_REDIRECT,
  qa_mode,
  proxyUrl,
  testing = false,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const email = params.get("email") || "";
  const qa = useMemo(
    () => (typeof qa_mode === "boolean" ? qa_mode : params.has("qa_mode") || testing),
    [qa_mode, params, testing]
  );

  const PROXY_URL =
    proxyUrl ||
    window.__UK_PROXY_URL__ ||
    `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });

  // Stripe fallback state
  const [needCardInfo, setNeedCardInfo] = useState(false);
  const [cardNumberReady, setCardNumberReady] = useState(false);
  const [cardExpiryReady, setCardExpiryReady] = useState(false);
  const [cardCvcReady, setCardCvcReady] = useState(false);

  const cardFilled = cardNumberReady && cardExpiryReady && cardCvcReady;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!productId) {
          setProduct(null);
          setAlert({ type: "error", message: "No product configured." });
          return;
        }
        const { data: payload } = await callApi(PROXY_URL, {
          method: "GET",
          path: `/get-products/${productId}`,
          qa_mode: qa,
        });
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];
        const found = list.find((p) => Number(p.id) === Number(productId)) || null;
        if (!cancelled) setProduct(found);
        if (!found && !cancelled) setAlert({ type: "error", message: "Product unavailable." });
      } catch {
        if (!cancelled) setAlert({ type: "error", message: "Failed to load product." });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId, PROXY_URL, qa]);

  const redirectWithEmail = () => {
    const base = (redirectUrl || DEFAULT_REDIRECT).trim();
    if (!base) return;
    try {
      const url = new URL(base, window.location.origin);
      if (email) url.searchParams.set("email", email);
      window.location.href = url.toString();
    } catch {
      window.location.href = base;
    }
  };

  async function createPaymentMethodFromElements() {
    if (!stripe || !elements) throw new Error("Stripe is not ready yet.");
    const cardEl = elements.getElement(CardNumberElement);
    if (!cardEl) throw new Error("Card element not mounted.");
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardEl,
    });
    if (error) throw new Error(error.message || "Failed to create payment method.");
    return paymentMethod; // full object (same as OneStep form)
  }

const onBuy = async () => {
  setAlert({ type: "", message: "" });

  // Basic guards
  if (!email)  { setAlert({ type: "error", message: "Missing email in URL." }); return; }
  if (!product){ setAlert({ type: "error", message: "Product unavailable." }); return; }
  if (!agree)  { setAlert({ type: "error", message: "Please agree to the charge first." }); return; }

  try {
    setSubmitting(true);

    // SECOND CLICK PATH (user already saw inputs and filled card)
    if (needCardInfo) {
      if (!stripe || !elements) {
        setAlert({ type: "error", message: "Stripe isn’t ready yet. Please try again in a second." });
        setSubmitting(false);
        return;
      }
      if (!cardFilled) {
        setAlert({ type: "error", message: "Please fill out the card details." });
        setSubmitting(false);
        return;
      }

      // Create PM and retry purchase with the new card
      const pm = await createPaymentMethodFromElements(); 
      const retry = await callApi(PROXY_URL, {
        method: "POST",
        path: upsellPurchasePath,
        qa_mode: qa,
        data: {
          email,
          product_id: Number(productId),
          paymentMethodId: pm.id,
          cardData: { id: pm.id, card: pm.card },
        },
        params: { email, product_id: Number(productId) },
      });

      if (retry?.data?.success || retry?.data?.id) {
        setAlert({ type: "success", message: "Added to your order!" });
        redirectWithEmail();
      } else {
        setAlert({ type: "error", message: retry?.data?.message || "Payment failed" });
      }
      setSubmitting(false);
      return;
    }

    // FIRST CLICK PATH (normal upsell attempt, no card yet)
    const attempt = await callApi(PROXY_URL, {
      method: "POST",
      path: upsellPurchasePath,
      qa_mode: qa,
      data: { email, product_id: Number(productId) },
      params: { email, product_id: Number(productId) },
    });

    if (attempt?.data?.success || attempt?.data?.id) {
      setAlert({ type: "success", message: "Added to your order!" });
      redirectWithEmail();
      setSubmitting(false);
      return;
    }

    // Determine if backend is telling us a card is required
    const apiMsg = attempt?.data?.message || attempt?.data?.error || "";
    const needsCard =
      String(apiMsg).toLowerCase().includes("no payment method") ||
      String(apiMsg).toLowerCase().includes("payment method") ||
      attempt?.status === 402;

    if (!needsCard) {
      setAlert({ type: "error", message: apiMsg || "Payment failed" });
      setSubmitting(false);
      return;
    }

    // SHOW CARD INPUTS and EXIT this click — second click will handle PM + retry
    setNeedCardInfo(true);
    setSubmitting(false);
    return;

  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Purchase error";

    if (String(msg).toLowerCase().includes("no payment method")) {
      setNeedCardInfo(true);
    }
    setAlert({ type: "error", message: msg });
    setSubmitting(false);
  }
};


  if (loading) return null;

  const priceNow = product ? getEffectivePrice(product) : 0;
  const priceText = `$${formatMoney(priceNow)}`;

  const buyDisabled =
    submitting ||
    !email ||
    !product ||
    !agree ||
    (needCardInfo && (!cardNumberReady || !cardExpiryReady || !cardCvcReady));

  return (
    <div className="upsell-wrap">
      <input type="hidden" name="email" value={email} />

      {alert.type === "error" && (
        <div className="alert alert-danger mt-3 mb-2" role="alert">{alert.message}</div>
      )}
      {alert.type === "success" && (
        <div className="alert alert-success mt-3 mb-2" role="alert">{alert.message}</div>
      )}

      <div className="upsell-agree">
        <input
          id="upsell-agree"
          type="checkbox"
          className="form-check-input"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
        />
        <label htmlFor="upsell-agree">
          By clicking on the order button below, I agree to be charged {priceText} on my credit card stored on file.
        </label>
      </div>

      {needCardInfo && (
        <div className="alert alert-warning mt-3 mb-2" role="alert">
          No payment method set up. Please fill out your card information
          <div className="row g-3 mt-3">
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">Card Number</label>
              <div className="osof-stripe">
                <CardNumberElement
                  options={stripeInputStyle}
                  onChange={(e) => setCardNumberReady(!e.empty)}
                />
              </div>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label fw-semibold">Expiration</label>
              <div className="osof-stripe">
                <CardExpiryElement
                  options={stripeInputStyle}
                  onChange={(e) => setCardExpiryReady(!e.empty)}
                />
              </div>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label fw-semibold">CVC</label>
              <div className="osof-stripe">
                <CardCvcElement
                  options={stripeInputStyle}
                  onChange={(e) => setCardCvcReady(!e.empty)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        className="upsell-btn"
        onClick={onBuy}
        disabled={buyDisabled}
      >
        {submitting ? (
          <span className="upsell-spinner" />
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="white" className="upsell-cart">
              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm0 2c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm10-2c-1.1 0-1.99.9-1.99 2S15.9 22 17 22s2-.9 2-2-.9-2-2-2zm0 2c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zM7.04 11h9.91c.82 0 1.54-.5 1.85-1.25L21.79 4H6.21l-.95-2H1v2h3l3.6 7.59-1.35 2.45C5.08 14.9 5 15.15 5 15.5c0 .83.67 1.5 1.5 1.5h12v-2H7.42c-.12 0-.25-.08-.32-.19L8.1 13h8.88c1.38 0 2.63-.86 3.12-2.15L22 4H6.21l.83 1.59h14.6c.17 0 .31-.1.38-.24.07-.13.05-.28-.05-.4L19.1 4H7.7l-.66 1.25C7.04 5.32 7 5.65 7 6v5zm10-1H8V6h9v4z"/>
            </svg>
            {priceText} - Complete Order
          </>
        )}
      </button>
    </div>
  );
}

/* =========================
   OUTER (loads Stripe & wraps)
   ========================= */
export default function UpsellOrderForm({
  productId,
  upsellPurchasePath = "/onclick-upsell",
  redirectUrl = DEFAULT_REDIRECT,
  qa_mode,
  proxyUrl,
  testing = false,
  // keep same env pattern as your OneStep form
  testKey = import.meta.env.VITE_STRIPE_STAGING_KEY,
  liveKey = import.meta.env.VITE_STRIPE_PROD_KEY,
}) {
  const qaModeURL =
    typeof window !== "undefined" &&
    (new URLSearchParams(window.location.search).get("qa_mode") === "on" ||
      new URLSearchParams(window.location.search).get("qa_mode") === "true" ||
      new URLSearchParams(window.location.search).get("testing") === "true");
  const useTest = Boolean(testing || qa_mode || qaModeURL);
  const activeKey = useTest ? testKey : liveKey;
  const stripePromise = useMemo(
    () => loadStripe(activeKey),
    [activeKey]
  );

  return (
    <Elements key={activeKey} stripe={stripePromise}>
      <UpsellOrderFormInner
        productId={productId}
        upsellPurchasePath={upsellPurchasePath}
        redirectUrl={redirectUrl}
        qa_mode={useTest}
        proxyUrl={proxyUrl}
        testing={useTest}
      />
    </Elements>
  );
}
